import logo from "@/assets/logo.png";

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-background p-8">
      <div className="max-w-3xl mx-auto space-y-8">
        <div className="space-y-2">
          <img src={logo} alt="Logo CDC" className="h-10 w-auto mb-4" />
          <h1 className="text-3xl font-bold">Política de Privacidad</h1>
          <p className="text-sm text-muted-foreground">
            Última actualización: julio de 2026
          </p>
        </div>

        <Section title="1. Responsable del tratamiento">
          <p>
            <strong>CDC — Central de Cargas</strong> es la organización responsable del tratamiento de los datos
            personales recopilados a través de la aplicación móvil y el sistema web de gestión (SCCR —
            Sistema Central de Control y Reconciliación).
          </p>
          <p className="mt-2">
            Para consultas sobre privacidad podés escribirnos a:{" "}
            <a href="mailto:eleiva@weikiu.com" className="text-primary underline underline-offset-4">
              eleiva@weikiu.com
            </a>
          </p>
        </Section>

        <Section title="2. Datos que recopilamos">
          <p>La aplicación recopila únicamente los datos necesarios para la gestión operativa de flotas y viajes:</p>
          <ul className="mt-2 list-disc pl-6 space-y-1 text-sm">
            <li>Nombre completo y DNI/CUIT del conductor o usuario registrado.</li>
            <li>Correo electrónico y número de teléfono (para autenticación y contacto).</li>
            <li>Datos de unidades (camión, semirremolque): patente, tipo, estado.</li>
            <li>Información de viajes: origen, destino, fechas, carga, documentos asociados (CRT, carta de porte).</li>
            <li>Registros de actividad dentro del sistema (auditoría de acciones).</li>
          </ul>
          <p className="mt-3">
            <strong>No recopilamos</strong> datos de geolocalización en tiempo real, datos biométricos ni información
            financiera de los conductores.
          </p>
        </Section>

        <Section title="3. Finalidad del tratamiento">
          <p>Los datos recopilados se utilizan exclusivamente para:</p>
          <ul className="mt-2 list-disc pl-6 space-y-1 text-sm">
            <li>Gestión operativa interna de flotas, viajes y liquidaciones.</li>
            <li>Control y reconciliación de documentos de carga (CRT / SmartGT).</li>
            <li>Comunicaciones operativas entre conductores y operadores.</li>
            <li>Generación de reportes y exportaciones para uso interno.</li>
            <li>Auditoría de acciones para seguridad y control interno.</li>
          </ul>
          <p className="mt-3">
            Los datos <strong>no son utilizados con fines comerciales</strong>, publicitarios ni son vendidos o
            cedidos a terceros fuera del grupo empresario.
          </p>
        </Section>

        <Section title="4. Acceso a los datos">
          <p>El acceso a la información está restringido según el rol asignado dentro del sistema:</p>
          <ul className="mt-2 list-disc pl-6 space-y-1 text-sm">
            <li><strong>Administrador:</strong> acceso completo para gestión y auditoría.</li>
            <li><strong>Operador:</strong> acceso a datos operativos de viajes, flotas y documentación.</li>
            <li><strong>Gestor de flota:</strong> acceso a las unidades y conductores bajo su cargo.</li>
            <li><strong>Conductor:</strong> acceso limitado a sus propios viajes y documentos.</li>
          </ul>
          <p className="mt-3">
            El personal externo, proveedores de tecnología y terceros <strong>no tienen acceso</strong> a los datos
            personales salvo que sea estrictamente necesario para el funcionamiento del servicio, y bajo
            acuerdo de confidencialidad.
          </p>
        </Section>

        <Section title="5. Conservación de los datos">
          <p>
            Los datos se conservan durante el tiempo necesario para cumplir con las finalidades descritas y con
            las obligaciones legales aplicables (normativa aduanera, laboral y fiscal argentina).
          </p>
          <p className="mt-2">
            Los registros de auditoría se conservan por un mínimo de <strong>2 años</strong>. Las cuentas de
            usuarios dados de baja se desactivan; los datos asociados se retienen en forma anonimizada para
            mantener la integridad histórica de los registros operativos.
          </p>
        </Section>

        <Section title="6. Seguridad">
          <p>Implementamos medidas técnicas y organizativas para proteger los datos, entre ellas:</p>
          <ul className="mt-2 list-disc pl-6 space-y-1 text-sm">
            <li>Autenticación mediante JWT con sesiones validadas en base de datos.</li>
            <li>Protección CSRF en todas las operaciones de escritura.</li>
            <li>Limitación de intentos de inicio de sesión (rate limiting).</li>
            <li>Comunicación cifrada mediante HTTPS.</li>
            <li>Registro de auditoría de todas las acciones sensibles.</li>
          </ul>
        </Section>

        <Section title="7. Derechos del usuario">
          <p>
            De acuerdo con la <strong>Ley 25.326 de Protección de Datos Personales</strong> de la República
            Argentina, los titulares de los datos tienen derecho a:
          </p>
          <ul className="mt-2 list-disc pl-6 space-y-1 text-sm">
            <li>Acceder a sus datos personales registrados en el sistema.</li>
            <li>Solicitar la rectificación de datos incorrectos o desactualizados.</li>
            <li>Solicitar la supresión de sus datos cuando ya no sean necesarios.</li>
          </ul>
          <p className="mt-3">
            Para ejercer estos derechos, enviar una solicitud a{" "}
            <a href="mailto:sistemas@cdc.com.ar" className="text-primary underline underline-offset-4">
              sistemas@cdc.com.ar
            </a>{" "}
            con asunto <em>"Datos personales — [nombre completo]"</em>.
          </p>
        </Section>

        <Section title="8. Cambios en esta política">
          <p>
            Podemos actualizar esta política periódicamente. Cuando lo hagamos, actualizaremos la fecha indicada
            al inicio del documento. El uso continuado de la aplicación tras la publicación de cambios implica la
            aceptación de la política actualizada.
          </p>
        </Section>

        <div className="border-t pt-6 text-xs text-muted-foreground">
          <p>CDC — Sistema Central de Control y Reconciliación (SCCR) · Argentina</p>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h2 className="text-lg font-semibold">{title}</h2>
      <div className="text-sm text-foreground/90 leading-relaxed">{children}</div>
    </section>
  );
}
